/**
 * OTOMATİK YEDEKLEYİCİ — React'siz çekirdek (P-a'nın `esitleyici.ts`i gibi).
 *
 * NE ZAMAN YAZAR:
 *  - açılışta (son yedekten beri defter değiştiyse — önceki oturumda yazım
 *    başarısız olduysa kaybolmasın),
 *  - defter değişince, `gecikmeMs` sonra (art arda on hareket girilirken on
 *    yükleme olmasın),
 *  - sekme arka plana geçerken bekleyen değişiklik varsa HEMEN,
 *  - "Şimdi yedekle" ile.
 * Periyodik yoklama YOK: değişmeyen defteri yeniden yazmanın anlamı yok.
 *
 * NE YAZAR: `parafon-<cihaz>-son.pfai` (her zaman en son hâl) ve
 * `parafon-<cihaz>-YYYY-MM-DD.pfai` (günün son hâli). Son `sakla` günlük
 * dosya tutulur, eskiler silinir. GÜNLÜK SÜRÜMLERİN SEBEBİ: otomatik yedek
 * bir HATAYI da otomatik yedekler — yanlışlıkla silinen yirmi hareket bir
 * dakika sonra "son" dosyaya da yansır. Dünün dosyası o hatanın geri dönüş
 * yolu. Cihaz etiketi aynı buluta iki tarayıcı yazdığında birinin
 * diğerinin dosyasını ezmemesi için.
 *
 * BOŞ DEFTER YAZILMAZ. Site verisi silinmiş bir tarayıcıda kasa da silindiği
 * için otomatik yedek zaten kapalıdır; ama başka bir yoldan boş bir defterle
 * kurulum yapılırsa, buluttaki son iyi yedeğin üzerine boş bir defter yazmak
 * tam olarak bu şeridin önlemek istediği kaybı üretirdi.
 *
 * DEĞİŞMEYEN DEFTER YAZILMAZ: dışa aktarmanın özeti (zaman damgası hariç)
 * son yedeğinkiyle aynıysa ağa hiç çıkılmaz. Eşitleme sonrası `revision`
 * artışları (fiyat geldi, defter değişmedi) böylece bedava.
 *
 * KİLİTLİ DEFTER (06.10.2026, `sifreliOlustur` verilince): şifreleme Worker'da
 * ana anahtarla (`.pfai` v2); kasada anahtar YOK (eski sürüm 1 kayıt ilk turda
 * çevrilir, anahtarı silinir). Adlar profilden türetilmiş önekle ve TARİHSİZ
 * (K5): `parafon-<önek>-son.pfai` ve `parafon-<önek>-gNN.pfai` — NN, günün
 * 30'luk döngüdeki yuvası; eski yuva kendiliğinden ezilir, budama gerekmez.
 * İlk v2 yedek yazılıp GERİ OKUNDUKTAN sonra bu cihazın eski biçimli
 * dosyaları silinir (D4).
 *
 * EŞİTLEME (08.10.2026): önek iki parçalı, `<defter>-<cihaz>`. Açılışta ve
 * sekme öne gelince (`esitle`) AYNI DEFTERİN öbür cihazlarının "son"
 * dosyaları okunup açık defterle BİRLEŞTİRİLİR (kayıt başına sonraki
 * değişiklik kazanır; silmeler iz olarak kalır, kayıp yok). Dosyanın bu
 * deftere ait olduğunu ad değil Worker söyler: açık defterin anahtarıyla
 * çözülmeyen dosya (başka defter) hiç birleştirilmez. Tek parçalı önekli eski
 * v2 dosyaları da aday; bu cihazınkiler yeni adla yazılıp geri okunduktan
 * sonra — ve YALNIZCA bu deftere aitse — silinir (`adGocu`). Defter
 * değişince (D6) defter parçası değişir: eski defterin bulut dosyaları ezilmez.
 */

import { M } from "../dil";
import type {
  BulutHedefi,
  HedefKaydi,
  HedefTuru,
  KasaKaydi,
  KasaKaydiV2,
  SonHata,
  SonYedek,
  YedekAnahtari,
} from "./tipler";
import type { YedekKasasi } from "./kasa";
import type { EsitlemeCakismasi, ImportResult } from "@parafon/contracts/local-api";

/**
 * Hedef, yazmak için kullanıcı etkileşimi gerektiğini bir işin ORTASINDA
 * öğrenirse (yenileme anahtarı iptal edildi, izin geri alındı) bunu fırlatır.
 * Hata değil DURUM: yedek bekler, "yeniden bağlan" düğmesi görünür.
 */
export class BaglantiGerekliHatasi extends Error {
  constructor(mesaj: string) {
    super(mesaj);
    this.name = "BaglantiGerekliHatasi";
  }
}

export interface YedekDurumu {
  /** `null` = kasa henüz okunmadı. */
  readonly kurulu: boolean | null;
  readonly hedefTuru: HedefTuru | null;
  readonly konum: string | null;
  readonly son: SonYedek | null;
  readonly hata: SonHata | null;
  /** Buluta yazılmamış değişiklik var. */
  readonly bekliyor: boolean;
  /** Yazmak için tek tıklık yeniden bağlanma gerekiyor. */
  readonly baglantiGerekli: boolean;
  readonly calisiyor: boolean;
  /** Son eşitleme turu: ne zaman, öbür cihazlardan kaç kayıt yazıldı. Hiç olmadıysa `null`. */
  readonly esitleme: {
    readonly zaman: string;
    readonly alinan: number;
    readonly dosya: number;
    /** Son eşitlemeden sonra iki cihazın da değiştirdiği kayıtlar (sonraki tutuldu). */
    readonly cakismalar: readonly EsitlemeCakismasi[];
  } | null;
}

export interface YedekleyiciBagimliliklari {
  readonly kasa: YedekKasasi;
  /** Kasadaki kayıttan canlı hedef üretir. */
  readonly hedefAc: (kayit: HedefKaydi) => BulutHedefi;
  /** Defterin düz JSON'u (`repo.exportPlainJson`). */
  readonly disaAktar: () => Promise<string>;
  readonly sifrele: (json: string, anahtar: YedekAnahtari) => Promise<string>;
  /** KİLİTLİ DEFTER: Worker'da ana anahtarla `.pfai` v2. Verilirse kasada anahtar tutulmaz. */
  readonly sifreliOlustur?: () => Promise<string>;
  /** KİLİTLİ DEFTER: dosya adlarının öneki, `<defter>-<cihaz>`. */
  readonly dosyaOneki?: () => Promise<string>;
  /** EŞİTLEME: aynı defterin öbür cihaz dosyasını açık defterle birleştirir (başka defterse fırlatır). */
  readonly esitle?: (metin: string) => Promise<{ readonly sonuc: ImportResult }>;
  /** Dosya açık deftere mi ait (ad göçünde yalnızca bunlar silinir). */
  readonly aitMi?: (metin: string) => Promise<boolean>;
  /** Tur bitti: çakışma izini ilerlet (bundan sonraki yerel değişiklikler aday). */
  readonly esitlemeIziniIlerlet?: () => Promise<void>;
  /** Eşitleme defteri değiştirdi (ekranlar yenilensin). */
  readonly esitlendi?: (alinan: number) => void;
  /** Dosya adındaki cihaz etiketi (`cihazEtiketi`). */
  readonly cihaz: string;
  readonly saat?: () => Date;
  readonly gecikmeMs?: number;
  /** Tutulacak günlük sürüm sayısı. */
  readonly sakla?: number;
}

export interface Yedekleyici {
  durum(): YedekDurumu;
  abone(dinleyici: (d: YedekDurumu) => void): () => void;
  /** Kasayı okur ve gerekiyorsa ilk yedeği alır. */
  baslat(): Promise<void>;
  /** Defter değişti — gecikmeli yedek planlar. */
  degisti(): void;
  /** Bekleyen gecikmeli yedeği hemen koşar (sekme gizlenirken). */
  bosalt(): Promise<void>;
  /** EŞİTLEME turu: aynı defterin öbür cihaz dosyalarını birleştirir. Hata fırlatmaz. */
  esitle(): Promise<void>;
  /** "Şimdi yedekle" — değişmemiş defteri de yazar. */
  simdi(): Promise<void>;
  /** Kullanıcı tıklamasıyla: hedefi yeniden yetkilendirir ve bekleyeni yazar. */
  yenidenBaglan(): Promise<void>;
  /** Yeni kurulum: kasaya yazar ve ilk yedeği HEMEN alır. Kilitli defterde anahtar `null`. */
  kur(anahtar: YedekAnahtari | null, hedef: HedefKaydi): Promise<void>;
  /**
   * KURULU yedeğin hedef kaydını değiştirir (kasa kodu yenilendi ya da öbür
   * cihazın yeni kodu girildi); son yedek bilgisi korunur, bekleyen yedek
   * yeni hedefe yazılır. Kurulu değilse hata.
   */
  hedefiDegistir(hedef: HedefKaydi): Promise<void>;
  /** Otomatik yedeği bu tarayıcıda kapatır. Buluttaki dosyalara DOKUNMAZ. */
  kaldir(): Promise<void>;
  kapat(): void;
}

export const VARSAYILAN_GECIKME_MS = 20_000;
export const VARSAYILAN_SAKLA = 30;

/** deviceId'den dosya adına girecek kısa, dosya sistemi güvenli etiket. */
export function cihazEtiketi(deviceId: string): string {
  const temiz = deviceId.replace(/[^A-Za-z0-9]/g, "").toLowerCase();
  return temiz.length === 0 ? "cihaz" : temiz.slice(-6);
}

export function sonDosyaAdi(cihaz: string): string {
  return `parafon-${cihaz}-son.pfai`;
}

export function gunlukDosyaAdi(cihaz: string, gun: string): string {
  return `parafon-${cihaz}-${gun}.pfai`;
}

/** Kilitli defterde günün yuvası: tarih adda değil (K5), 30'luk döngü. */
export function gunYuvasi(gun: string, sakla: number): string {
  const n = Math.floor(Date.parse(`${gun}T00:00:00Z`) / 86_400_000);
  return `g${String(n % sakla).padStart(2, "0")}`;
}

/** Bu cihazın ESKİ biçimli dosyaları (D4: ilk v2 yedekten sonra silinir). */
export function eskiDosyaMi(ad: string, cihaz: string): boolean {
  return new RegExp(`^parafon-${cihaz}-(son|\\d{4}-\\d{2}-\\d{2})\\.pfai$`).test(ad);
}

/** Öneki `<defter>-<cihaz>` parçalarına ayırır (eski tek parçalıda defter `null`). */
export function onekParcalari(onek: string): { readonly defter: string | null; readonly cihaz: string } {
  const i = onek.indexOf("-");
  return i < 0 ? { defter: null, cihaz: onek } : { defter: onek.slice(0, i), cihaz: onek.slice(i + 1) };
}

/**
 * Eşitleme adayı mı: aynı defterin BAŞKA cihazının "son" dosyası (yeni ad) ya
 * da tek parçalı önekli eski v2 "son" dosyası (sahibi Worker'da anlaşılır).
 */
export function esitlemeAdayi(ad: string, defter: string, cihaz: string): boolean {
  const yeni = /^parafon-([0-9a-f]{10})-([0-9a-f]{10})-son\.pfai$/.exec(ad);
  if (yeni !== null) return yeni[1] === defter && yeni[2] !== cihaz;
  const eski = /^parafon-([0-9a-f]{10})-son\.pfai$/.exec(ad);
  return eski !== null && eski[1] !== cihaz;
}

/** Bu cihazın tek parçalı önekli eski v2 dosyası mı (ad göçünün adayı). */
export function eskiOnekliMi(ad: string, cihaz: string): boolean {
  return new RegExp(`^parafon-${cihaz}-(son|g\\d{2})\\.pfai$`).test(ad);
}

function yazilan(r: ImportResult): number {
  return (
    r.accounts.written + r.transactions.written + r.manualValuations.written + r.manualPrices.written + r.settings.written
  );
}

/** Yerel takvim günü (toISOString UTC verir; İstanbul'da gece yarısından sonra dün olurdu). */
function yerelGun(t: Date): string {
  const y = String(t.getFullYear());
  const m = String(t.getMonth() + 1).padStart(2, "0");
  const d = String(t.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

interface Ozet {
  readonly ozet: string;
  /** Canlı (silinmemiş) kayıt yok. */
  readonly bos: boolean;
}

const KAYIT_LISTELERI = ["accounts", "transactions", "manualValuations", "manualPrices"] as const;

/**
 * Dışa aktarmanın ÖZETİ — zaman damgası HARİÇ. `exportedAt` her çağrıda
 * değiştiği için dahil edilseydi "değişmedi" sorusunun cevabı hep "değişti"
 * olurdu.
 */
export async function defterOzeti(json: string): Promise<Ozet> {
  const nesne = JSON.parse(json) as Record<string, unknown>;
  delete nesne["exportedAt"];
  let canli = 0;
  for (const alan of KAYIT_LISTELERI) {
    const liste = nesne[alan];
    if (!Array.isArray(liste)) continue;
    for (const k of liste) {
      if (typeof k === "object" && k !== null && (k as { deleted?: unknown }).deleted !== true) {
        canli += 1;
      }
    }
  }
  const bayt = new TextEncoder().encode(JSON.stringify(nesne));
  const hash = new Uint8Array(await globalThis.crypto.subtle.digest("SHA-256", bayt));
  let hex = "";
  for (const b of hash) hex += b.toString(16).padStart(2, "0");
  return { ozet: hex, bos: canli === 0 };
}

function mesajOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function createYedekleyici(b: YedekleyiciBagimliliklari): Yedekleyici {
  const saat = b.saat ?? (() => new Date());
  const gecikme = b.gecikmeMs ?? VARSAYILAN_GECIKME_MS;
  const sakla = b.sakla ?? VARSAYILAN_SAKLA;
  const kilitli = b.sifreliOlustur !== undefined;

  let durum: YedekDurumu = {
    kurulu: null,
    hedefTuru: null,
    konum: null,
    son: null,
    hata: null,
    bekliyor: false,
    baglantiGerekli: false,
    calisiyor: false,
    esitleme: null,
  };
  const dinleyiciler = new Set<(d: YedekDurumu) => void>();
  let zamanlayici: ReturnType<typeof setTimeout> | null = null;
  let ucustaki: Promise<void> | null = null;
  interface Bekleyen {
    readonly resolve: () => void;
    readonly reject: (e: unknown) => void;
  }
  let tekrar: { zorla: boolean; bosIseHata: boolean; bekleyenler: Bekleyen[] } | null = null;
  let kapali = false;

  function guncelle(p: Partial<YedekDurumu>): void {
    durum = { ...durum, ...p };
    for (const d of dinleyiciler) d(durum);
  }

  function kayittanDurum(k: KasaKaydi | null, hedef: BulutHedefi | null): Partial<YedekDurumu> {
    if (k === null) {
      return { kurulu: false, hedefTuru: null, konum: null, son: null, hata: null, bekliyor: false, baglantiGerekli: false };
    }
    return {
      kurulu: true,
      hedefTuru: k.hedef.tur,
      konum: hedef?.konum ?? null,
      son: k.son,
      hata: k.hata,
    };
  }

  function zamanlayiciyiIptal(): void {
    if (zamanlayici !== null) {
      clearTimeout(zamanlayici);
      zamanlayici = null;
    }
  }

  /** Tek iş. Hataları KASAYA ve duruma yazar, fırlatmaz — `zorla` hariç. */
  async function is(zorla: boolean, bosIseHata: boolean): Promise<void> {
    let kayit = await b.kasa.oku();
    if (kayit === null) {
      guncelle(kayittanDurum(null, null));
      return;
    }
    if (kilitli && kayit.surum === 1) {
      // Eski kayıt: anahtar KASADAN SİLİNİR (tarayıcıya erişen biri onunla
      // eski yedekleri çözebilirdi); hedef ve son yedek bilgisi kalır. Bu tur
      // defter değişmemiş olsa da YAZAR: bulutta hemen v2 bir kopya olsun ve
      // eski dosyaların temizliği (D4) kullanıcı bir şey değiştirene kadar
      // beklemesin.
      kayit = { surum: 2, hedef: kayit.hedef, son: kayit.son, hata: kayit.hata, eskiTemizlendi: false };
      await b.kasa.yaz(kayit);
      zorla = true;
    }
    const hedef = b.hedefAc(kayit.hedef);
    guncelle({ ...kayittanDurum(kayit, hedef), calisiyor: true });

    const json = await b.disaAktar();
    const { ozet, bos } = await defterOzeti(json);
    if (!zorla && kayit.son?.ozet === ozet) {
      guncelle({ bekliyor: false });
      return;
    }
    if (bos) {
      guncelle({ bekliyor: false });
      if (bosIseHata) {
        throw new Error(M.yedek.kayit.kasaBos);
      }
      return;
    }

    try {
      if ((await hedef.hazirlik()) === "etkilesim-gerekli") {
        await b.kasa.yaz({ ...kayit, hedef: hedef.kayit() });
        guncelle({ bekliyor: true, baglantiGerekli: true });
        return;
      }
      const t = saat();
      let gunluk: string;
      let son: string;
      let metin: string;
      if (kilitli && b.sifreliOlustur !== undefined && b.dosyaOneki !== undefined) {
        metin = await b.sifreliOlustur();
        const onek = await b.dosyaOneki();
        gunluk = `parafon-${onek}-${gunYuvasi(yerelGun(t), sakla)}.pfai`;
        son = `parafon-${onek}-son.pfai`;
      } else {
        if (kayit.surum !== 1) throw new Error(M.yedek.kayit.anahtarYok);
        metin = await b.sifrele(json, kayit.anahtar);
        gunluk = gunlukDosyaAdi(b.cihaz, yerelGun(t));
        son = sonDosyaAdi(b.cihaz);
      }
      // Önce günlük, sonra "son": arada kesilirse en kötü ihtimalle "son" bir
      // öncekidir; tersi sırada günlük sürüm eksik kalırdı.
      await hedef.yaz(gunluk, metin);
      await hedef.yaz(son, metin);
      let eskiTemizlendi = kayit.surum === 2 ? kayit.eskiTemizlendi : false;
      let adGocu = kayit.surum === 2 ? kayit.adGocu === true : false;
      if (kayit.surum === 2) {
        if (!eskiTemizlendi) eskiTemizlendi = await eskileriTemizle(hedef, son, metin);
        if (!adGocu) adGocu = await eskiOnekliTemizle(hedef, son, metin);
      } else {
        await buda(hedef);
      }
      const yeni: KasaKaydi =
        kayit.surum === 2
          ? { ...kayit, hedef: hedef.kayit(), son: { zaman: t.toISOString(), ozet, dosya: gunluk }, hata: null, eskiTemizlendi, adGocu }
          : { ...kayit, hedef: hedef.kayit(), son: { zaman: t.toISOString(), ozet, dosya: gunluk }, hata: null };
      await b.kasa.yaz(yeni);
      guncelle({ ...kayittanDurum(yeni, hedef), bekliyor: false, baglantiGerekli: false });
    } catch (e: unknown) {
      if (e instanceof BaglantiGerekliHatasi) {
        await b.kasa.yaz({ ...kayit, hedef: hedef.kayit() });
        guncelle({ bekliyor: true, baglantiGerekli: true });
        return;
      }
      const hata: SonHata = { zaman: saat().toISOString(), mesaj: mesajOf(e) };
      await b.kasa.yaz({ ...kayit, hedef: hedef.kayit(), hata });
      guncelle({ hata, bekliyor: true });
      if (zorla) throw e;
    }
  }

  /**
   * D4: v2 "son" dosyası yazıldı ve GERİ OKUNUP birebir aynı çıktıysa bu
   * cihazın eski biçimli dosyaları silinir. Okuma tutmazsa ya da liste
   * alınamazsa hiçbir şey silinmez, bir sonraki turda yeniden denenir.
   * Başka cihazların dosyalarına DOKUNULMAZ (ad cihaz etiketini taşır).
   */
  async function eskileriTemizle(hedef: BulutHedefi, son: string, metin: string): Promise<boolean> {
    try {
      if ((await hedef.oku(son)) !== metin) return false;
      const liste = await hedef.listele();
      for (const d of liste) {
        if (eskiDosyaMi(d.ad, b.cihaz)) await hedef.sil(d.ad);
      }
      return true;
    } catch {
      return false;
    }
  }

  /**
   * AD GÖÇÜ: yeni adlı "son" yazıldı ve geri okunup aynı çıktıysa bu cihazın
   * tek parçalı önekli eski v2 dosyalarından YALNIZCA bu deftere ait olanlar
   * silinir. Başka bir defterinkiler (D6'dan önceki defterin yedeği) kalır.
   * Liste ya da okuma düşerse hiçbir şey silinmez; bir sonraki turda yeniden.
   */
  async function eskiOnekliTemizle(hedef: BulutHedefi, son: string, metin: string): Promise<boolean> {
    if (b.dosyaOneki === undefined || b.aitMi === undefined) return true;
    try {
      const { defter, cihaz } = onekParcalari(await b.dosyaOneki());
      if (defter === null) return true;
      if ((await hedef.oku(son)) !== metin) return false;
      for (const d of await hedef.listele()) {
        if (!eskiOnekliMi(d.ad, cihaz)) continue;
        if (await b.aitMi(await hedef.oku(d.ad))) await hedef.sil(d.ad);
      }
      return true;
    } catch {
      return false;
    }
  }

  let esitlemeUcusta: Promise<void> | null = null;

  /** Eşitleme turları üst üste binmez: uçuştaki tur varsa onu bekler. */
  function esitleTek(): Promise<void> {
    if (kapali) return Promise.resolve();
    esitlemeUcusta ??= esitlemeTuru().finally(() => {
      esitlemeUcusta = null;
    });
    return esitlemeUcusta;
  }

  /** Bir eşitleme turu. Ağ/izin sorunu turu sessizce bırakır — yedek yolu onu zaten söylüyor. */
  async function esitlemeTuru(): Promise<void> {
    if (!kilitli || b.esitle === undefined || b.dosyaOneki === undefined) return;
    const kayit = await b.kasa.oku();
    if (kayit === null) return;
    const hedef = b.hedefAc(kayit.hedef);
    try {
      if ((await hedef.hazirlik()) === "etkilesim-gerekli") return;
      const { defter, cihaz } = onekParcalari(await b.dosyaOneki());
      if (defter === null) return;
      const adaylar = (await hedef.listele()).filter((d) => esitlemeAdayi(d.ad, defter, cihaz));
      let alinan = 0;
      const cakismalar: EsitlemeCakismasi[] = [];
      for (const d of adaylar) {
        try {
          const { sonuc } = await b.esitle(await hedef.oku(d.ad));
          alinan += yazilan(sonuc);
          cakismalar.push(...(sonuc.cakismalar ?? []));
        } catch {
          // başka defter ya da okunamayan dosya: bu dosya atlanır
        }
      }
      // İz TUR sonunda ilerler: aynı turdaki ikinci dosya da ilk dosyadan
      // önceki yerel değişiklikleri çakışma adayı olarak görsün.
      await b.esitlemeIziniIlerlet?.();
      guncelle({ esitleme: { zaman: saat().toISOString(), alinan, dosya: adaylar.length, cakismalar } });
      if (alinan > 0) b.esitlendi?.(alinan);
    } catch {
      // liste alınamadı / bağlantı gerekli: tur atlanır
    }
  }

  /** Budama başarısızlığı yedeği BOZMAZ: fazladan dosya kalır, o kadar. */
  async function buda(hedef: BulutHedefi): Promise<void> {
    const desen = new RegExp(`^parafon-${b.cihaz}-\\d{4}-\\d{2}-\\d{2}\\.pfai$`);
    let liste: readonly { ad: string }[];
    try {
      liste = await hedef.listele();
    } catch {
      return;
    }
    const gunlukler = liste
      .map((d) => d.ad)
      .filter((ad) => desen.test(ad))
      .sort()
      .reverse();
    for (const ad of gunlukler.slice(sakla)) {
      try {
        await hedef.sil(ad);
      } catch {
        // bir sonraki turda yeniden denenir
      }
    }
  }

  function calistir(zorla: boolean, bosIseHata = false): Promise<void> {
    if (kapali) return Promise.resolve();
    zamanlayiciyiIptal();
    if (ucustaki !== null) {
      // Uçuştaki iş, onu tetikleyen değişikliği görmemiş olabilir: bitince
      // bir tur daha. İstekler BİRLEŞİR (zorla yükseltilir, düşürülmez) ve
      // çağıran o EK turun sonucunu bekler — "Şimdi yedekle"nin sözü, kendi
      // isteğini görmemiş bir işin bitişiyle tutulmuş olmasın.
      const onceki = tekrar;
      const birlesik = {
        zorla: zorla || (onceki?.zorla ?? false),
        bosIseHata: bosIseHata || (onceki?.bosIseHata ?? false),
        bekleyenler: [...(onceki?.bekleyenler ?? [])],
      };
      tekrar = birlesik;
      return new Promise<void>((resolve, reject) => {
        birlesik.bekleyenler.push({ resolve, reject });
      });
    }
    const p = (async () => {
      try {
        await is(zorla, bosIseHata);
      } finally {
        guncelle({ calisiyor: false });
        ucustaki = null;
        const sonra = tekrar;
        tekrar = null;
        if (sonra !== null) {
          calistir(sonra.zorla, sonra.bosIseHata).then(
            () => {
              for (const b of sonra.bekleyenler) b.resolve();
            },
            (e: unknown) => {
              for (const b of sonra.bekleyenler) b.reject(e);
            },
          );
        }
      }
    })();
    ucustaki = p;
    return p;
  }

  return {
    durum: () => durum,
    abone(dinleyici) {
      dinleyiciler.add(dinleyici);
      return () => dinleyiciler.delete(dinleyici);
    },
    async baslat() {
      // Önce öbür cihazların değişiklikleri: ilk yedek birleşmiş defteri yazsın.
      if (kilitli && b.esitle !== undefined) await esitleTek();
      try {
        await calistir(false);
      } catch (e: unknown) {
        guncelle({ hata: { zaman: saat().toISOString(), mesaj: mesajOf(e) } });
      }
    },
    degisti() {
      if (kapali || durum.kurulu !== true) return;
      guncelle({ bekliyor: true });
      zamanlayiciyiIptal();
      zamanlayici = setTimeout(() => {
        zamanlayici = null;
        void calistir(false).catch(() => undefined);
      }, gecikme);
    },
    async bosalt() {
      if (zamanlayici === null) return;
      await calistir(false).catch(() => undefined);
    },
    esitle: esitleTek,
    simdi: () => calistir(true, true),
    async yenidenBaglan() {
      const kayit = await b.kasa.oku();
      if (kayit === null) return;
      const hedef = b.hedefAc(kayit.hedef);
      await hedef.yenidenBaglan();
      await b.kasa.yaz({ ...kayit, hedef: hedef.kayit() });
      guncelle({ baglantiGerekli: false });
      await calistir(false);
    },
    async kur(anahtar, hedef) {
      const kayit: KasaKaydi =
        kilitli || anahtar === null
          ? ({ surum: 2, hedef, son: null, hata: null, eskiTemizlendi: false } satisfies KasaKaydiV2)
          : { surum: 1, anahtar, hedef, son: null, hata: null };
      await b.kasa.yaz(kayit);
      guncelle({ kurulu: true, hedefTuru: hedef.tur, son: null, hata: null, baglantiGerekli: false });
      await calistir(true);
    },
    async hedefiDegistir(hedef) {
      const kayit = await b.kasa.oku();
      if (kayit === null) throw new Error(M.yedek.kayit.kuruluDegil);
      await b.kasa.yaz({ ...kayit, hedef, hata: null });
      guncelle({ ...kayittanDurum({ ...kayit, hedef, hata: null }, b.hedefAc(hedef)), baglantiGerekli: false });
      await calistir(false);
    },
    async kaldir() {
      zamanlayiciyiIptal();
      await b.kasa.sil();
      guncelle(kayittanDurum(null, null));
    },
    kapat() {
      kapali = true;
      zamanlayiciyiIptal();
      dinleyiciler.clear();
    },
  };
}
